/* KLIK homepage (premium): collection tabs (keyboard and RTL aware, lazy panels), live showroom status, and a quiet
   reveal as sections come into view. Only the first tab's products are in the page; the others load the first time
   they are wanted (tap, hover or keyboard focus) from the klik-hp-rail section. */
(() => {
  "use strict";
  const rtl = (el) => getComputedStyle(el).direction === "rtl";
  const arabic = () => (document.documentElement.lang || "").toLowerCase().startsWith("ar");

  /* ---------- reveal on scroll ---------- */
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!reduce && "IntersectionObserver" in window) {
    // brand rollout: blocks already on screen (or above it) are marked shown BEFORE the hidden state exists, so the first
    // screen never blinks out and back in when this deferred script runs.
    document.querySelectorAll("[data-pm-reveal]").forEach((el) => { if (el.getBoundingClientRect().top < innerHeight * 0.95) el.classList.add("is-in"); });
    document.documentElement.classList.add("pm-motion");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        io.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
    const watch = (root) => root.querySelectorAll("[data-pm-reveal]:not(.is-in)").forEach((el) => io.observe(el));
    watch(document);
    // Anything already on screen (or above it) appears at once.
    requestAnimationFrame(() => document.querySelectorAll("[data-pm-reveal]").forEach((el) => { if (el.getBoundingClientRect().top < innerHeight * 0.95) el.classList.add("is-in"); }));
  }

  /* ---------- collection tabs ---------- */
  const jobs = new WeakMap();
  function load(panel) {
    if (!panel || !panel.dataset.pmSrc || panel.dataset.pmLoaded) return Promise.resolve();
    if (jobs.has(panel)) return jobs.get(panel);
    const url = new URL(panel.dataset.pmSrc, location.origin),
      preview = new URLSearchParams(location.search).get("preview_theme_id");
    url.searchParams.set("section_id", "klik-hp-rail");
    if (preview) url.searchParams.set("preview_theme_id", preview);
    const grid = panel.querySelector("[data-pm-grid]");
    const job = fetch(url.href, { credentials: "same-origin" })
      .then((response) => (response.ok ? response.text() : Promise.reject(Error(String(response.status)))))
      .then((html) => {
        const fragment = new DOMParser().parseFromString(html, "text/html").querySelector("[data-pm-fragment]");
        if (!fragment || !fragment.children.length) throw Error("empty");
        [...fragment.children].forEach((card) => grid.appendChild(document.importNode(card, true)));
        panel.dataset.pmLoaded = "true";
      })
      .catch(() => {
        // The "View all" button still leads to the full collection.
        delete panel.dataset.pmSrc;
      })
      .finally(() => {
        grid.querySelectorAll(".pm-skeleton").forEach((s) => s.remove());
        jobs.delete(panel);
      });
    jobs.set(panel, job);
    return job;
  }
  document.querySelectorAll("[data-pm-tabs]").forEach((root) => {
    const tabs = [...root.querySelectorAll('[role="tab"]')],
      panelOf = (tab) => document.getElementById(tab.getAttribute("aria-controls"));
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab,
          panel = panelOf(t);
        t.setAttribute("aria-selected", String(on));
        t.tabIndex = on ? 0 : -1;
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
      tab.scrollIntoView({ block: "nearest", inline: "nearest" });
      load(panelOf(tab));
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => select(tab));
      ["pointerenter", "touchstart", "focus"].forEach((name) => tab.addEventListener(name, () => load(panelOf(tab)), { passive: true }));
      tab.addEventListener("keydown", (event) => {
        const forward = rtl(tab) ? "ArrowLeft" : "ArrowRight",
          back = rtl(tab) ? "ArrowRight" : "ArrowLeft";
        let j = null;
        if (event.key === forward) j = i + 1;
        else if (event.key === back) j = i - 1;
        else if (event.key === "Home") j = 0;
        else if (event.key === "End") j = tabs.length - 1;
        if (j === null) return;
        event.preventDefault();
        select(tabs[(j + tabs.length) % tabs.length], true);
      });
    });
  });

  /* ---------- showroom status (hours from the page's structured data, Riyadh time) ---------- */
  const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const DAYS_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
  const minutes = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  function stores() {
    const found = [];
    const visit = (node) => {
      if (!node || typeof node !== "object") return;
      if (Array.isArray(node)) return node.forEach(visit);
      const city = node.address && node.address.addressLocality;
      if (city && node.openingHoursSpecification) found.push({ city: String(city).toLowerCase(), specs: [].concat(node.openingHoursSpecification) });
      Object.values(node).forEach((value) => typeof value === "object" && visit(value));
    };
    document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
      try {
        visit(JSON.parse(script.textContent));
      } catch (error) {
        /* ignore malformed blocks */
      }
    });
    return found;
  }
  function riyadhNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Riyadh", weekday: "long", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    return { day: DAYS.indexOf(parts.weekday), min: Number(parts.hour) * 60 + Number(parts.minute) };
  }
  function slots(specs) {
    const out = [];
    specs.forEach((s) => {
      const open = minutes(s.opens),
        close = minutes(s.closes);
      if (open === null || close === null) return;
      [].concat(s.dayOfWeek || []).forEach((d) => {
        const day = DAYS.indexOf(String(d).replace(/^https?:\/\/schema\.org\//, ""));
        if (day >= 0) out.push({ day, open, close });
      });
    });
    return out.sort((a, b) => a.day - b.day || a.open - b.open);
  }
  function clock(min, ar) {
    const h = Math.floor(min / 60) % 24,
      m = String(min % 60).padStart(2, "0"),
      h12 = h % 12 || 12;
    return ar ? `${h12}:${m} ${h < 12 ? "ص" : "م"}` : `${h12}:${m} ${h < 12 ? "am" : "pm"}`;
  }
  function statusText(list, now, ar) {
    const today = list.filter((s) => s.day === now.day);
    const current = today.find((s) => now.min >= s.open && now.min < s.close);
    if (current) return { open: true, text: ar ? `مفتوح الآن · حتى ${clock(current.close, ar)}` : `Open now · until ${clock(current.close, ar)}` };
    const later = today.find((s) => s.open > now.min);
    if (later) return { open: false, text: ar ? `مغلق الآن · يفتح ${clock(later.open, ar)}` : `Closed now · opens ${clock(later.open, ar)}` };
    for (let k = 1; k <= 7; k++) {
      const day = (now.day + k) % 7,
        first = list.find((s) => s.day === day);
      if (!first) continue;
      const when = k === 1 ? (ar ? "غدًا" : "tomorrow") : ar ? DAYS_AR[day] : DAYS[day];
      return { open: false, text: ar ? `مغلق الآن · يفتح ${when} ${clock(first.open, ar)}` : `Closed now · opens ${when} ${clock(first.open, ar)}` };
    }
    return null;
  }
  function paintHours() {
    const root = document.querySelector("[data-pm-hours]");
    if (!root) return;
    let data;
    try {
      data = stores();
    } catch (error) {
      return;
    }
    if (!data.length) return;
    const now = riyadhNow(),
      ar = root.getAttribute("dir") === "rtl" || arabic();
    root.querySelectorAll("[data-pm-city]").forEach((card) => {
      const store = data.find((s) => s.city === card.dataset.pmCity.toLowerCase()),
        out = card.querySelector("[data-pm-status]");
      if (!store || !out) return;
      const status = statusText(slots(store.specs), now, ar);
      if (!status) return;
      out.textContent = status.text;
      out.classList.toggle("is-open", status.open);
      out.hidden = false;
    });
  }
  paintHours();
  setInterval(paintHours, 60000);
})();
